import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X, Plus, Trash2, Save, ShoppingBag,
  Building, Calendar, FileText, User, CreditCard,
  Info, DollarSign, Hash, Settings, Phone, CheckCircle2,
  ArrowDown, Edit
} from 'lucide-react';
import { Modal, FormInput, FormSelect, FormTextarea, Button, SearchableSelect } from '@/components/ui';
import { fmt } from '@/lib/formatters';
import api from '@/lib/API';
import { toast } from 'react-hot-toast';

const SalesForm = ({ isOpen, onClose, onSaleSuccess, dynamicSettings, initialData = null, purchases = [] }) => {
  const [loading, setLoading] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const isEdit = !!initialData;
  const [expandedIdx, setExpandedIdx] = useState(null);
  const scrollRef = useRef(null);

  const [header, setHeader] = useState({
    invoiceNo: '',
    date: new Date().toISOString().split('T')[0],
    customer: '',
    customerPhone: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingCity: '',
    billingState: '',
    billingPincode: '',
    deliveryContactPerson: '',
    deliveryPhone: '',
    deliveryAddressLine1: '',
    deliveryAddressLine2: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryPincode: '',
    store: '',
    saleType: 'cash'
  });

  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [items, setItems] = useState([]);
  const [charges, setCharges] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [adjustment, setAdjustment] = useState(0);

  // State for the single item currently being configured
  const initialCurrentItem = {
    _id: '', name: '', qty: 1, unit: '', rate: 0,
    discountPercent: 0, taxPercent: 0, taxAmount: 0, amount: 0,
    itemCode: '', itemType: '', category: '', brand: ''
  };
  const [currentItem, setCurrentItem] = useState({ ...initialCurrentItem });

  useEffect(() => {
    if (isOpen) {
      extractItemsFromPurchases();
      if (initialData) {
        setHeader({
          invoiceNo: initialData.invoiceNo || '',
          date: initialData.date ? new Date(initialData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          customer: initialData.customer || '',
          customerPhone: initialData.customerPhone || '',
          billingAddressLine1: initialData.billingAddressLine1 || '',
          billingAddressLine2: initialData.billingAddressLine2 || '',
          billingCity: initialData.billingCity || '',
          billingState: initialData.billingState || '',
          billingPincode: initialData.billingPincode || '',
          deliveryContactPerson: initialData.deliveryContactPerson || '',
          deliveryPhone: initialData.deliveryPhone || '',
          deliveryAddressLine1: initialData.deliveryAddressLine1 || '',
          deliveryAddressLine2: initialData.deliveryAddressLine2 || '',
          deliveryCity: initialData.deliveryCity || '',
          deliveryState: initialData.deliveryState || '',
          deliveryPincode: initialData.deliveryPincode || '',
          store: typeof initialData.store === 'object' ? initialData.store?._id : (initialData.store || '')
        });
        
        // Auto-detect if same as billing
        const isSame = 
          (!initialData.deliveryAddressLine1 && !initialData.deliveryCity) ||
          (initialData.billingAddressLine1 === initialData.deliveryAddressLine1 && 
           initialData.billingCity === initialData.deliveryCity);
        setSameAsBilling(isSame);
        if (initialData.items?.length) {
          setItems(initialData.items.map(i => ({
            id: i._id || Math.random(),
            _id: typeof i.item === 'object' ? i.item?._id : i.item,
            name: typeof i.item === 'object' ? (i.item?.name || i.item?.itemName) : 'Unknown Item',
            qty: i.qty, unit: i.unit, rate: i.rate,
            discountPercent: i.discountPercent || 0,
            taxPercent: i.taxPercent || 0,
            taxAmount: i.taxAmount || 0, amount: i.amount
          })));
        }
        if (initialData.charges?.length) setCharges(initialData.charges.map(c => ({ ...c, id: c._id || Math.random() })));
        setRemarks(initialData.remarks || '');
        setAdjustment(initialData.adjustment || 0);
      } else {
        setHeader({ invoiceNo: '', date: new Date().toISOString().split('T')[0], customer: '', customerPhone: '', billingAddressLine1: '', billingAddressLine2: '', billingCity: '', billingState: '', billingPincode: '', deliveryContactPerson: '', deliveryPhone: '', deliveryAddressLine1: '', deliveryAddressLine2: '', deliveryCity: '', deliveryState: '', deliveryPincode: '', store: '' });
        setSameAsBilling(true);
        setItems([]);
        setCharges([]);
        setRemarks('');
        setAdjustment(0);
      }
      setCurrentItem({ ...initialCurrentItem });
      setExpandedIdx(null);
    }
  }, [isOpen, initialData, purchases]);

  const extractItemsFromPurchases = () => {
    const extracted = [];
    purchases.forEach(p => {
      if (p.items && Array.isArray(p.items)) {
        p.items.forEach(it => {
          if (it.item && typeof it.item === 'object') {
            if (!extracted.find(x => x._id === it.item._id)) {
              extracted.push({
                ...it.item,
                purchaseAmount: it.purchaseRate || it.rate || 0,
                stockQty: it.quantity || it.qty || 0
              });
            } else {
              const existingItem = extracted.find(x => x._id === it.item._id);
              existingItem.stockQty += (it.quantity || it.qty || 0);
            }
          }
        });
      }
    });
    setInventoryItems(extracted);
  };

  const handleSelectItem = (selectedId) => {
    const sel = inventoryItems.find(i => i._id === selectedId);
    if (sel) {
      const getStr = (v) => (typeof v === 'object' && v !== null) ? (v.title || v.name || v.unitName || v.uom || '') : v;
      setCurrentItem({
        ...currentItem,
        _id: sel._id,
        name: sel.name || sel.itemName || 'Unknown',
        unit: sel.units?.title || sel.units?.name || getStr(sel.unit) || getStr(sel.uom) || getStr(sel.units) || '',
        rate: sel.purchaseAmount || 0,
        taxPercent: sel.taxPercent || 0,
        qty: 1,
        itemCode: sel.itemCode || sel.code || '',
        stockQty: sel.stockQty || 0,
        itemType: typeof sel.category === 'object' ? (sel.category?.type || 'Product') : 'Product',
        category: typeof sel.category === 'object' ? (sel.category?.categoryName || sel.category?.title) : sel.category,
        brand: typeof sel.brand === 'object' ? (sel.brand?.brandName || sel.brand?.title) : sel.brand
      });
    } else {
      setCurrentItem({ ...currentItem, _id: '', name: selectedId });
    }
  };

  const updateCurrentItem = (field, value) => {
    setCurrentItem(prev => {
      const updated = { ...prev, [field]: value };
      const qty = Number(updated.qty) || 0;
      const rate = Number(updated.rate) || 0;
      const discPer = Number(updated.discountPercent) || 0;
      const taxPer = Number(updated.taxPercent) || 0;
      const base = qty * rate;
      updated.amount = base - (base * discPer) / 100;
      updated.taxAmount = (updated.amount * taxPer) / 100;
      return updated;
    });
  };

  const handleAddItemToInvoice = () => {
    if (!currentItem._id && !currentItem.name) {
      toast.error("Please select or enter an item name");
      return;
    }
    if (Number(currentItem.qty) <= 0) {
      toast.error("Quantity must be greater than 0");
      return;
    }
    if (currentItem.stockQty !== undefined && Number(currentItem.qty) > currentItem.stockQty) {
      toast.error(`Quantity exceeds available stock (${currentItem.stockQty})`);
      return;
    }
    
    // Check if we are editing an existing item in the list or adding new
    if (currentItem.id) {
        setItems(items.map(i => i.id === currentItem.id ? { ...currentItem } : i));
    } else {
        setItems([...items, { ...currentItem, id: Date.now() }]);
    }
    
    setCurrentItem({ ...initialCurrentItem });
    setExpandedIdx(null);
  };

  const handleEditItem = (id) => {
      const itemToEdit = items.find(i => i.id === id);
      if (itemToEdit) {
          setCurrentItem({...itemToEdit});
          scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
  };

  const handleRemoveItem = (id) => {
    setItems(items.filter(i => i.id !== id));
  };

  const handleAddCharge = () => setCharges([...charges, { id: Date.now(), name: '', amount: 0 }]);

  const totals = useMemo(() => {
    const taxable = items.reduce((s, r) => s + r.amount, 0);
    const totalTax = items.reduce((s, r) => s + r.taxAmount, 0);
    const totalDiscount = items.reduce((s, r) => s + ((Number(r.qty) * Number(r.rate)) * (Number(r.discountPercent) || 0) / 100), 0);
    const extraCharges = charges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const netBill = taxable + totalTax + extraCharges;
    const netPayable = netBill + (Number(adjustment) || 0);
    return { taxable, totalTax, totalDiscount, extraCharges, netBill, netPayable };
  }, [items, charges, adjustment]);

  const handleSubmit = async () => {
    if (!header.store || !header.customer || !header.invoiceNo) {
      toast.error('Store, Customer and Invoice No are required');
      return;
    }
    if (!items.length) { toast.error('Add at least one item to the invoice'); return; }
    setLoading(true);
    try {
      let finalHeader = { ...header };
      if (sameAsBilling) {
        finalHeader.deliveryContactPerson = header.customer;
        finalHeader.deliveryPhone = header.customerPhone;
        finalHeader.deliveryAddressLine1 = header.billingAddressLine1;
        finalHeader.deliveryAddressLine2 = header.billingAddressLine2;
        finalHeader.deliveryCity = header.billingCity;
        finalHeader.deliveryState = header.billingState;
        finalHeader.deliveryPincode = header.billingPincode;
      }

      const payload = {
        ...finalHeader,
        remarks,
        items: items.map(r => ({
          item: r._id || null, // Allow custom items without _id if needed, but backend schema requires ObjectId. We should assume _id exists.
          qty: r.qty, unit: r.unit || 'Unit',
          rate: r.rate, taxPercent: r.taxPercent, taxAmount: r.taxAmount,
          amount: r.amount, discountPercent: r.discountPercent
        })),
        charges: charges.map(c => ({ name: c.name || 'Charge', amount: Number(c.amount) || 0 })),
        totalDiscount: totals.totalDiscount,
        taxableAmount: totals.taxable,
        totalTax: totals.totalTax,
        netBill: totals.netBill,
        adjustment: Number(adjustment) || 0,
        netPayable: totals.netPayable,
      };

      const res = isEdit 
        ? await api.put(`/inventory/sales/${initialData._id}`, payload)
        : await api.post('/inventory/sales', payload);

      if (res.status === 200 || res.status === 201 || res.data) {
        toast.success(isEdit ? 'Sale updated successfully' : 'Sale recorded successfully');
        if (typeof onSaleSuccess === 'function') onSaleSuccess();
        if (typeof onClose === 'function') onClose();
      }
    } catch (err) {
      toast.error(err.message || (isEdit ? 'Failed to update sale' : 'Failed to record sale'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      className="max-w-[830px]"
      title={
        <div className="flex items-center gap-2">
          <div>
            <h2 className="font-semibold text-lg text-brand-dark">{isEdit ? 'Edit Sale' : 'Record New Sale'}</h2>
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full max-h-[85vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto pr-2 no-scrollbar pb-6 space-y-6">

          {/* 01. Sales Details Grid */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>01. Sales Details</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <FormInput
                label="Invoice Number"
                value={header.invoiceNo}
                onChange={e => setHeader({ ...header, invoiceNo: e.target.value })}
                placeholder="e.g. INV-001"
                required={true}
              />
              <FormInput
                label="Sale Date"
                type="date"
                value={header.date}
                onChange={e => setHeader({ ...header, date: e.target.value })}
                required={true}
              />
              <FormInput
                label="Customer Name"
                value={header.customer}
                onChange={e => setHeader({ ...header, customer: e.target.value })}
                placeholder="Enter customer name"
                required={true}
              />
              <FormInput
                label="Phone"
                value={header.customerPhone}
                onChange={e => setHeader({ ...header, customerPhone: e.target.value })}
                placeholder="Mobile number"
              />
              <FormInput
                label="Address Line 1"
                value={header.billingAddressLine1}
                onChange={e => setHeader({ ...header, billingAddressLine1: e.target.value })}
                placeholder="Flat, House no., Building"
              />
              <FormInput
                label="Address Line 2"
                value={header.billingAddressLine2}
                onChange={e => setHeader({ ...header, billingAddressLine2: e.target.value })}
                placeholder="Area, Street, Sector"
              />
              <FormInput
                label="District / City"
                value={header.billingCity}
                onChange={e => setHeader({ ...header, billingCity: e.target.value })}
                placeholder="e.g. Hyderabad"
              />
              <FormInput
                label="State"
                value={header.billingState}
                onChange={e => setHeader({ ...header, billingState: e.target.value })}
                placeholder="e.g. Telangana"
              />
              <FormInput
                label="PIN Code"
                value={header.billingPincode}
                onChange={e => setHeader({ ...header, billingPincode: e.target.value })}
                placeholder="e.g. 500001"
              />
              
              {/* Shipping Checkbox */}
              <div className="col-span-1 sm:col-span-2 pt-2">
                <label className="flex items-center gap-2 cursor-pointer w-fit">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue/20 cursor-pointer"
                    checked={sameAsBilling}
                    onChange={(e) => setSameAsBilling(e.target.checked)}
                  />
                  <span className="text-[13px] font-medium text-slate-700">Shipping Address is same as Billing Address</span>
                </label>
              </div>

              {!sameAsBilling && (
                <>
                  <div className="col-span-1 sm:col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 mt-2"><span>Shipping Details</span></div>
                  <FormInput
                    label="Contact Person"
                    value={header.deliveryContactPerson}
                    onChange={e => setHeader({ ...header, deliveryContactPerson: e.target.value })}
                    placeholder="Receiver Name"
                  />
                  <FormInput
                    label="Contact Phone"
                    value={header.deliveryPhone}
                    onChange={e => setHeader({ ...header, deliveryPhone: e.target.value })}
                    placeholder="Receiver Phone"
                  />
                  <FormInput
                    label="Shipping Address Line 1"
                    value={header.deliveryAddressLine1}
                    onChange={e => setHeader({ ...header, deliveryAddressLine1: e.target.value })}
                  />
                  <FormInput
                    label="Shipping Address Line 2"
                    value={header.deliveryAddressLine2}
                    onChange={e => setHeader({ ...header, deliveryAddressLine2: e.target.value })}
                  />
                  <FormInput
                    label="Shipping City"
                    value={header.deliveryCity}
                    onChange={e => setHeader({ ...header, deliveryCity: e.target.value })}
                  />
                  <FormInput
                    label="Shipping State"
                    value={header.deliveryState}
                    onChange={e => setHeader({ ...header, deliveryState: e.target.value })}
                  />
                  <FormInput
                    label="Shipping PIN Code"
                    value={header.deliveryPincode}
                    onChange={e => setHeader({ ...header, deliveryPincode: e.target.value })}
                  />
                </>
              )}

              <FormSelect
                label="Internal Store Location"
                value={header.store}
                onChange={e => setHeader({ ...header, store: e.target.value })}
                required={true}
              >
                <option value="">Select Store</option>
                {dynamicSettings?.stores?.map(s => (
                  <option key={s._id} value={s._id}>{s.storeName || s.title} ({s.storeCode || s.code || s.storeId})</option>
                ))}
              </FormSelect>
            </div>
          </div>

          {/* 02. Item Configuration */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>02. Item Configuration</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div className="relative">
                <SearchableSelect
                  label="Item Name"
                  placeholder="Enter item name or search..."
                  value={currentItem._id || currentItem.name}
                  onChange={handleSelectItem}
                  allowCustom={true}
                  options={inventoryItems.map(item => ({
                    value: item._id,
                    label: item.itemName || item.name || 'Unnamed Item',
                    dropdownLabel: (
                      <div className="flex flex-col gap-1 w-full text-left">
                        <div className="text-[11px] font-black text-slate-800 uppercase group-hover:text-brand-blue transition-colors">
                          {item.itemName || item.name || 'Unnamed Item'}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[9px] font-black text-brand-blue bg-blue-50 px-1.5 py-0.5 rounded tracking-widest uppercase">{item.itemCode || item.code || 'MASTER'}</span>
                          <span className="text-[9px] text-slate-400 font-bold uppercase">
                             Stock: {item.stockQty || 0}
                          </span>
                        </div>
                      </div>
                    )
                  }))}
                  required={true}
                  className="w-full relative z-[9999]"
                />
              </div>

              <FormInput
                label="Item Type"
                value={currentItem.itemType || ''}
                readOnly={true}
                inputClassName="bg-slate-50 text-slate-400 cursor-not-allowed"
              />

              <FormInput
                label="Category"
                value={currentItem.category || ''}
                readOnly={true}
                inputClassName="bg-slate-50 text-slate-400 cursor-not-allowed"
              />

              <FormInput
                label="Brand"
                value={currentItem.brand || ''}
                readOnly={true}
                inputClassName="bg-slate-50 text-slate-400 cursor-not-allowed"
              />

              <FormInput
                label="Item Code / SKU"
                value={currentItem.itemCode || ''}
                readOnly={true}
                inputClassName="bg-slate-50 text-slate-400 cursor-not-allowed"
              />

              <FormInput
                label="Unit"
                value={currentItem.unit}
                onChange={e => updateCurrentItem('unit', e.target.value)}
                placeholder="e.g. PCS, KG"
              />
            </div>
          </div>

          {/* 03. Billing Details */}
          <div className="space-y-6">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>03. Billing Details</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <FormInput
                label="Sale Rate (₹)"
                type="number"
                value={currentItem.rate}
                onChange={e => updateCurrentItem('rate', e.target.value)}
                placeholder="0.00"
                required={true}
              />
              <FormInput
                label="Quantity"
                type="number"
                value={currentItem.qty}
                onChange={e => updateCurrentItem('qty', e.target.value)}
                placeholder="1"
                required={true}
              />
              <FormInput
                label="Discount (%)"
                type="number"
                value={currentItem.discountPercent}
                onChange={e => updateCurrentItem('discountPercent', e.target.value)}
                placeholder="0"
              />
              <FormSelect
                label="Tax Percentage (%)"
                value={currentItem.taxPercent}
                onChange={e => updateCurrentItem('taxPercent', e.target.value)}
                options={[
                    { value: 0, label: "0%" },
                    { value: 5, label: "5%" },
                    { value: 12, label: "12%" },
                    { value: 18, label: "18%" },
                    { value: 28, label: "28%" }
                ]}
              />

              <FormInput
                label="Tax Amount (₹)"
                type="number"
                value={currentItem.taxAmount}
                readOnly
                inputClassName="bg-slate-50 font-bold text-blue-600 cursor-not-allowed"
              />

              <FormInput
                label="Net Item Amount (Taxable + Tax)"
                type="number"
                value={currentItem.amount + currentItem.taxAmount}
                readOnly
                inputClassName="bg-slate-50 font-bold text-slate-700 cursor-not-allowed"
              />
            </div>
          </div>

          {/* Add to Invoice Action */}
          <div className="pt-4 pb-2">
            <Button 
              variant="secondary" 
              fullWidth 
              onClick={handleAddItemToInvoice}
              className="py-5 font-black text-[12px] uppercase tracking-[0.3em] shadow-xl shadow-blue-100 rounded-[1.25rem]"
              leftIcon={<Plus size={18} />}
            >
              {currentItem.id ? "Update Item in Invoice" : "Add Item to Invoice"}
            </Button>
          </div>

          {/* Invoice Items List Container */}
          {items.length > 0 && (
            <div className="space-y-5 pt-6">
              <div className="flex items-center justify-between px-1">
                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                   <div className="w-1.5 h-1.5 rounded-full bg-brand-blue" />
                   Items in Invoice ({items.length})
                </h4>
                <div className="text-[10px] font-black text-brand-blue bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
                   Subtotal: ₹{fmt(items.reduce((s, i) => s + (i.amount + i.taxAmount), 0))}
                </div>
              </div>
              <div className="space-y-4">
                {items.map((item, idx) => {
                  const isExpanded = expandedIdx === idx;
                  return (
                    <div key={item.id} className={`group rounded-[2rem] border transition-all duration-500 overflow-hidden ${isExpanded ? 'border-brand-blue bg-blue-50/20 ring-4 ring-brand-blue/5' : 'border-slate-100 bg-white hover:border-slate-300 shadow-sm'}`}>
                      <div className="p-6 flex items-center justify-between cursor-pointer" onClick={() => setExpandedIdx(isExpanded ? null : idx)}>
                        <div className="flex items-center gap-5">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-black transition-all ${isExpanded ? 'bg-brand-blue text-white shadow-xl rotate-12' : 'bg-slate-100 text-slate-400'}`}>{idx + 1}</div>
                          <div>
                            <div className="text-sm font-black text-slate-800 uppercase tracking-tight">{item.name}</div>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className="text-[10px] font-black text-brand-blue uppercase">{item.qty} {item.unit}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-8">
                           <div className="text-right">
                              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 opacity-60">Total</div>
                              <div className="text-xl font-black text-slate-800 tracking-tighter">₹{fmt(item.amount + item.taxAmount)}</div>
                           </div>
                           <div className={`transition-all duration-500 ${isExpanded ? 'rotate-180 text-brand-blue scale-125' : 'text-slate-300'}`}>
                             <ArrowDown size={20} />
                           </div>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-8 pb-8 pt-4 border-t border-brand-blue/10 animate-in slide-in-from-top-4 duration-500">
                           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                              <div className="space-y-1.5">
                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Unit Details</div>
                                <div className="text-xs font-black text-slate-700 italic">₹{fmt(item.rate)} / {item.unit}</div>
                              </div>
                              <div className="space-y-1.5">
                                <div className="text-[9px] font-black text-blue-500 uppercase tracking-[0.2em]">Discounts</div>
                                <div className="text-xs font-black text-blue-600">{item.discountPercent}% off</div>
                              </div>
                              <div className="space-y-1.5">
                                <div className="text-[9px] font-black text-blue-500 uppercase tracking-[0.2em]">Tax Breakdown</div>
                                <div className="text-xs font-black text-blue-600">{item.taxPercent}% GST (₹{fmt(item.taxAmount)})</div>
                              </div>
                              <div className="flex items-center justify-end gap-3">
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEditItem(item.id); }} className="w-10 h-10 rounded-xl text-blue-500 bg-blue-50 hover:bg-blue-500 hover:text-white transition-all"><Edit size={16} /></Button>
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }} className="w-10 h-10 rounded-xl text-rose-500 bg-rose-50 hover:bg-rose-500 hover:text-white transition-all"><Trash2 size={16} /></Button>
                              </div>
                           </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 04. Other Charges & Net Bill */}
          <div className="space-y-6 pt-6 border-t border-slate-100">
            <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-1 flex items-center gap-2"><span>04. Other Charges & Notes</span></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-600 uppercase tracking-widest">Service Charges & Extra Expenses</label>
                {charges.map(charge => (
                  <div key={charge.id} className="flex items-center gap-3">
                    <input placeholder="Charge Name" className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold outline-none focus:border-brand-blue" value={charge.name} onChange={e => setCharges(charges.map(c => c.id === charge.id ? { ...c, name: e.target.value } : c))} />
                    <input type="number" placeholder="0.00" className="w-32 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm font-black text-slate-700 outline-none focus:border-brand-blue" value={charge.amount} onChange={e => setCharges(charges.map(c => c.id === charge.id ? { ...c, amount: e.target.value } : c))} />
                    <Button variant="ghost" size="icon-sm" onClick={() => setCharges(charges.filter(c => c.id !== charge.id))} className="text-slate-300 hover:text-red-500"><Trash2 size={16} /></Button>
                  </div>
                ))}
                <button type="button" onClick={handleAddCharge} className="text-[10px] font-black text-brand-blue flex items-center gap-1 mt-2 hover:underline">
                  <Plus size={12} /> ADD CHARGE
                </button>
              </div>
              
              <div className="space-y-6">
                <FormInput
                  label="Adjustments (+/-)"
                  type="number"
                  placeholder="0"
                  value={adjustment}
                  onChange={e => setAdjustment(e.target.value)}
                />
                <FormTextarea
                  label="Remarks & Notes"
                  placeholder="Add any sale notes..."
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Final Summary Highlight */}
          <div className="pt-8">
            <div className="p-6 rounded-lg bg-brand-blue/5 border border-brand-blue/10 flex flex-col md:flex-row md:items-center justify-between gap-10 group">
               <div className="space-y-2 relative z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse" />
                    <div className="text-[10px] font-black text-brand-blue uppercase tracking-[0.4em]">Final Net Payment</div>
                  </div>
                  <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest leading-relaxed">Inclusive of all taxes and adjustments</p>
               </div>
               <div className="text-4xl font-black text-brand-blue tracking-tighter relative z-10">
                  ₹{fmt(totals.netPayable)}
               </div>
            </div>
          </div>
        </div>

        {/* Primary Action Buttons */}
        <div className="flex gap-4 pt-8 mt-4 border-t border-slate-100 bg-white px-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading} className="py-5 font-black text-[12px] uppercase tracking-[0.3em] rounded-[1.25rem]">Discard</Button>
          <Button 
            variant="primary" 
            fullWidth 
            onClick={handleSubmit} 
            loading={loading}
            className="py-5 font-black text-[12px] uppercase tracking-[0.3em] shadow-2xl shadow-blue-100 rounded-[1.25rem]"
            leftIcon={<CheckCircle2 size={18} />}
          >
            Complete Sales Entry
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SalesForm;
